import {
  DependencyList,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { LOAD_FAILED, loadedAll, settleAll } from 'app/state/utils/load-result'

export interface PrerequisiteReadsOptions {
  /** The reads run again whenever these change (a modal that stays mounted reruns them on each open). */
  deps?: DependencyList
  /** False while the reads are not wanted (a closed modal): nothing runs, `ready` is false. */
  enabled?: boolean
  /**
   * The answers, in `start`'s order, once every read succeeded — the caller
   * keeps what ITS read returned (a list loader answers the dispatched action
   * with the list as `payload`) rather than reading a shared store slice that
   * another form's later answer may overwrite.
   */
  onLoaded?: (results: unknown[]) => void
}

/**
 * Whether the reads a form or modal needs before it may open have all
 * succeeded. `start` fires them (leave out a read that is not needed);
 * `onFailed` runs when any failed, rejected, or could not even be started —
 * the caller says so and closes, because a failed read is not "no draft" and
 * a Save would create a second one. A late answer never reports into a
 * component or an opening that has since gone.
 */
export function usePrerequisiteReads(
  start: () => unknown[],
  onFailed: () => void,
  { deps = [], enabled = true, onLoaded }: PrerequisiteReadsOptions = {},
): boolean {
  // `ready` is cleared by the effect, which runs AFTER the render that
  // changed `deps` or `enabled`: that render would otherwise show the form as
  // ready with the previous opening's data (the payment modal reopened for
  // another loan would initialise a create form for one render — and a Save
  // in it would create a second draft). So `ready` remembers which inputs it
  // was earned for, and counts only while those are the current ones.
  const [readyFor, setReadyFor] = useState<DependencyList | null>(null)
  const inputs: DependencyList = [enabled, ...deps]
  // the latest callbacks, of a COMMITTED render: a render React discarded
  // must not leave its callbacks behind, so the refs are written in a layout
  // effect, not during render
  const onFailedRef = useRef(onFailed)
  const onLoadedRef = useRef(onLoaded)
  useLayoutEffect(() => {
    onFailedRef.current = onFailed
    onLoadedRef.current = onLoaded
  })

  useEffect(() => {
    if (!enabled) return undefined
    let cancelled = false
    const started = inputs
    setReadyFor(null)
    // a `start` that throws (a loan without a facility id, say) is a read
    // that failed before it left, not a loader for ever
    let reads: unknown[]
    try {
      reads = start()
    } catch {
      reads = [LOAD_FAILED]
    }
    settleAll(reads).then(results => {
      if (cancelled) return
      if (!loadedAll(results)) {
        onFailedRef.current()
        return
      }
      onLoadedRef.current?.(results)
      setReadyFor(started)
    })
    return () => {
      cancelled = true
    }
  }, [enabled, ...deps])

  return enabled && readyFor !== null && sameList(readyFor, inputs)
}

const sameList = (left: DependencyList, right: DependencyList): boolean =>
  left.length === right.length &&
  left.every((value, index) => Object.is(value, right[index]))
